import React, { useState, useEffect } from "react";
import { Snackbar, IconButton, Alert, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useGridApiRef } from '@mui/x-data-grid-pro';
import ControlledDataGrid from '../../../components/DataGrid/ControlledDataGrid';
import { ProgramFilter, ReportFilterBar } from '../ReportFilters';
import moment from "moment";
import axios from "axios";
import { APIURL } from "../../../config";

export default function PlanRenewalReport(props) {
  const defaultGridId = 38;
  const [gridId, setGridId] = useState(props.customReportGridId || defaultGridId);
  const apiRef = useGridApiRef();

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [reportDetails, setReportDetails] = useState({});
  const [isFavorite, setIsFavorite] = useState(false);
  const [successText, setSuccessText] = useState("");
  const [errorText, setErrorText] = useState("");
  const [selectedProgram, setSelectedProgram] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    axios
      .get(APIURL + "/jsreport/getAllReports", {
        params: { empID: props.user.emp_id },
      })
      .then((response) => {
        const report = response?.data?.reports.find((ele) => ele.report_name === "Plan Renewal Report");
        setReportDetails(report);
        setIsFavorite(report ? (report.status === 1 ? true : false) : false);
      });

    axios
      .get(APIURL + `/reports/getPlanRenewalReport`, {
        params: {
          company_id: props.user.comp_id,
          emp_id: props.user.emp_id,
          program_id: selectedProgram || undefined,
        },
      })
      .then((response) => {
        setData(response?.data.length !== 0 ?
          [
            ...response?.data?.current_months_reports,
            ...response?.data?.next_month_reports,
            ...response?.data?.reports_beyond_next_month_in_current_quarter,
          ]
          :
          []);
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 1 },
    { field: "ddd_id", headerName: "DDD ID", flex: 1 },
    { field: "program", headerName: "Program", flex: 1 },
    { field: "customer_name", headerName: "Customer Name", flex: 1 },
    { field: "coordinator_name", headerName: "Coordinator Name", flex: 1 },
    { field: "dob", headerName: "DOB", flex: 1 },
    { field: "plan_approval_date", headerName: "Plan Approval Date", flex: 1 },
    { field: "plan_start_date", headerName: "Plan Start Date", flex: 1 },
    { field: "plan_end_date", headerName: "Plan End Date", flex: 1 },
  ]

  const rows = data.map((inv, index) => {
    return {
      rowId: inv?.pro_id + Math.random().toString(36).substring(2, 12) + index,
      hierarchy: [inv.reports_span, inv.pro_title + " - " + inv.pro_id],
      id: inv?.pro_id,
      ddd_id: inv?.DDDID,
      program: inv?.Program,
      customer_name: inv?.cus_name || "",
      coordinator_name: inv?.employees ? inv?.employees.map(e => e.employee_name).join(", ") : "",
      dob: inv?.dob ? moment(inv?.dob).utc().format("MM/DD/YYYY") : "",
      plan_approval_date: inv?.plan_approval_date ? moment(inv?.plan_approval_date).utc().format("MM/DD/YYYY") : "",
      plan_start_date: inv?.pro_starts_on ? moment(inv?.pro_starts_on).utc().format("MM/DD/YYYY") : "",
      plan_end_date: inv?.pro_ends_on ? moment(inv?.pro_ends_on).utc().format("MM/DD/YYYY") : "",
    };
  });

  const markFavourite = async (data, type) => {
    const payload = {
      report_id: data?.report_id,
      status: type,
      empID: props.user.emp_id
    };

    axios
      .put(APIURL + "/jsreport/markFavorite", payload)
      .then((response) => {
        if (response?.status === 200 && response?.data?.code === 200) {
          setSuccessText(response?.data?.status_message || "Favorite Modified Successfully");
        } else {
          setErrorText("Something went wrong");
        }

        fetchData();
      });
  }

  return (
    <div style={{ margin: 0, width: '100%', padding: 10, boxSizing: 'border-box' }}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <ReportFilterBar onSearch={() => fetchData()}>
            <ProgramFilter
              comp_id={props.user.comp_id}
              value={selectedProgram}
              onChange={setSelectedProgram}
            />
          </ReportFilterBar>
          <Tooltip title={isFavorite ? "Remove from favorite reports" : "Add to favorite reports"}>
            <img
              src={isFavorite ? "./star-solid.svg" : "./star-regular.svg"}
              alt="barchart"
              style={{
                fontFamily: "Lato",
                width: "1.5rem",
                height: "1.5rem",
                cursor: "pointer",
                margin: 5
              }}
              onClick={() => markFavourite(reportDetails, isFavorite ? 0 : 1)}
            />
          </Tooltip>
        </div>
        <div style={{ height: 'calc(100vh - 250px)' }}>
          <ControlledDataGrid
            gridId={gridId}
            user={props.user}
            initialState={{ sorting: { sortModel: [{ field: "customer_name", sort: "asc" }] } }}
            apiRef={apiRef}
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            treeData
            getTreeDataPath={(row) => row.hierarchy}
            getRowId={(row) => row.rowId}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 1 ? 'report-row-alternate' : ''
            }
            loading={isLoading}
            sx={{
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'normal',
                lineHeight: 1.2,
                textAlign: 'left',
                wordBreak: 'break-word',
                maxWidth: '100%',
              },
              '& .report-row-alternate .MuiDataGrid-cell, & .report-row-alternate .MuiDataGrid-filler--pinnedLeft': {
                backgroundColor: '#EAF4FC',
              },
              '& .report-row-alternate:hover .MuiDataGrid-cell, & .report-row-alternate:hover .MuiDataGrid-filler--pinnedLeft': {
                backgroundColor: '#DCEFFC',
              },
            }}
          />
        </div>
      </div>
      <Snackbar
        open={successText !== ""}
        autoHideDuration={30000}
        onClose={() => setSuccessText("")}
      >
        <Alert
          sx={{ width: "100%" }}
          severity={"success"}
          elevation={6}
          variant="filled"
        >
          {successText}
          <IconButton
            aria-label="close"
            color="inherit"
            sx={{ p: 0.25 }}
            onClick={() => setSuccessText("")}
          >
            <CloseIcon />
          </IconButton>
        </Alert>
      </Snackbar>
      <Snackbar
        open={errorText !== ""}
        autoHideDuration={30000}
        onClose={() => setErrorText("")}
      >
        <Alert
          sx={{ width: "100%" }}
          severity={"error"}
          elevation={6}
          variant="filled"
        >
          {errorText}
          <IconButton
            aria-label="close"
            color="inherit"
            sx={{ p: 0.25 }}
            onClick={() => setErrorText("")}
          >
            <CloseIcon />
          </IconButton>
        </Alert>
      </Snackbar>
    </div>
  );
}
